import type { VerticalCopy } from "@/lib/verticalCopy";

/**
 * What the live completion screen says, the moment an applicant submits.
 *
 * ## Why this is its own module
 *
 * `ApplicantFlow` returns early for a completed live session, before its main
 * render, so nothing after that `return` ever runs on this screen. The
 * vertical's own completion copy (`checkpointIntro.complete`) lived after it,
 * behind the same condition, and was unreachable in live mode: a recruiting
 * applicant who had read "Trust Checkpoint" on every screen was thanked for an
 * "Identity Verification", and told the employer had "everything needed" while
 * their vouch was still outstanding.
 *
 * Deciding the copy here, and having the early return call this, means the
 * branch that actually renders is the one that reads the vertical.
 *
 * ## Leasing is unchanged, word for word
 *
 * A vertical with no `checkpointIntro` gets exactly the sentence the inline
 * JSX produced, including its two fallbacks. The leasing wording is not
 * endorsed by leaving it: "everything needed" has the same problem there. It
 * is left because leasing and Rockwell copy do not move in this change.
 */
export interface LiveCompletionScreen {
  title: string;
  /** Paragraphs, rendered in order. */
  body: string[];
  /** A smaller closing line, when the vertical has one. */
  footnote?: string;
}

/**
 * @param firstName The component's own value: the applicant's first name, or
 *   the literal `"there"` it uses when it has none.
 * @param orgName `session.org_name`, which can be absent.
 */
export function liveCompletionScreen(
  copy: VerticalCopy,
  firstName: string,
  orgName: string | undefined,
): LiveCompletionScreen {
  const checkpoint = copy.checkpointIntro;
  if (checkpoint) {
    // ⚠️ `complete.h2` is deliberately NOT used here. It reads "We've notified
    // the recruiter that you completed your Trust Checkpoint", and on this
    // screen that is not ours to assert. The org notification is best-effort:
    // it swallows its own errors so it can never block completion, which means
    // a failed send is silent by design. A side effect that is allowed to fail
    // quietly cannot be reported to the applicant as done. (Checked against UAT
    // 2026-09-22: the explicit Submit route sent no notification at all, which
    // is its own bug, but the reasoning here holds even once that is fixed.)
    return {
      title: checkpoint.complete.h1,
      body: [checkpoint.complete.cardBody],
      footnote: checkpoint.complete.cardFootnote,
    };
  }
  const thanks = firstName !== "there" ? `Thanks, ${firstName}.` : "Thanks.";
  return {
    title: "Identity Verification is complete",
    body: [
      `${thanks} Your additional evidence has been submitted. ${orgName ?? "The review team"} now has everything needed to continue your application — no further action required.`,
    ],
  };
}
